<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import type { Toast } from '../model/state'
import { useToast } from '../model/useToast'
import { CloseIcon, ErrorIcon, InfoCircleIcon, SuccessIcon, WarningIcon } from '@/shared/icons'

const { progressToast } = useToast()

const progress = ref(100)

const { variant, title, message, id } = defineProps<Toast>()

const items = {
  error: {
    icon: ErrorIcon,
    color: 'text-red-500',
    bg: 'bg-red-500',
  },
  success: {
    icon: SuccessIcon,
    color: 'text-green-500',
    bg: 'bg-green-500',
  },
  warning: {
    icon: WarningIcon,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500',
  },
  info: {
    icon: InfoCircleIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-500',
  },
}
const emit = defineEmits<{
  close: [id: string]
  pause: [id: string]
  resume: [id: string]
  progress: [id: string]
}>()

let frameId: number

const updateProgress = () => {
  progress.value = progressToast(id)
  frameId = requestAnimationFrame(updateProgress)
}

onMounted(() => updateProgress())

onUnmounted(() => cancelAnimationFrame(frameId))
</script>
<template>
  <div
    class="border rounded-lg p-2 w-[300px] flex items-center justify-between relative shadow-md bg-white overflow-hidden duration-300 ease-in-out"
    @mouseenter="emit('pause', id)"
    @mouseleave="emit('resume', id)"
  >
    <div class="flex w-full gap-2 p-2 items-center">
      <div class="text-red">
        <component :is="items[variant].icon" :class="items[variant].color" />
      </div>
      <div class="flex flex-col">
        <span>{{ title }}</span>
        <span class="text-sm">{{ message }}</span>
      </div>
      <div
        :class="[
          items[variant].bg,
          'progress-bar h-1 w-full absolute bottom-0 left-0 rounded-b-lg',
        ]"
        :style="{
          width: `${progress}%`,
        }"
      ></div>
    </div>
    <CloseIcon
      @click="emit('close', id)"
      class="size-4 text-gray-500 cursor-pointer hover:text-gray-900 self-start"
    />
  </div>
</template>
